import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { FirebaseError } from 'firebase/app';
import { auth, db, storage } from './firebase';
import { OrderDocument, OrderDocumentType } from '../types';

const MAX_DOCUMENT_SIZE_BYTES = 15 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const ALLOWED_EXTENSIONS = new Set([
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'doc',
  'docx',
  'xls',
  'xlsx',
]);

const convertTimestamp = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  if (typeof timestamp?.toDate === 'function') return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;

  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const sanitizeFileName = (fileName: string): string => {
  return fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
};

const getExtension = (fileName: string): string => {
  return fileName.split('.').pop()?.toLowerCase() || '';
};

const validateFile = (file: File): void => {
  if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
    throw new Error('File size too large. Maximum size is 15MB.');
  }

  const extension = getExtension(file.name);
  const mimeAllowed = !!file.type && ALLOWED_MIME_TYPES.has(file.type);
  const extensionAllowed = ALLOWED_EXTENSIONS.has(extension);

  if (!mimeAllowed && !extensionAllowed) {
    throw new Error('Unsupported file type. Allowed: PDF, PNG, JPG/JPEG, DOC/DOCX, XLS/XLSX.');
  }
};

const mapOrderDocument = (orderId: string, docId: string, data: any): OrderDocument => ({
  id: docId,
  orderId,
  type: (data.type || 'other') as OrderDocumentType,
  title: data.title || data.fileName || 'Untitled document',
  fileName: data.fileName || '',
  storagePath: data.storagePath || '',
  downloadUrl: data.downloadUrl || '',
  contentType: data.contentType || '',
  sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : 0,
  createdAt: convertTimestamp(data.createdAt),
  updatedAt: convertTimestamp(data.updatedAt),
  uploadedByUid: data.uploadedByUid || undefined,
  uploadedByName: data.uploadedByName || undefined,
});

export interface CreateOrderDocumentInput {
  orderId: string;
  file: File;
  type: OrderDocumentType;
  title?: string;
  uploadedByName?: string;
}

export interface UpdateOrderDocumentInput {
  type?: OrderDocumentType;
  title?: string;
}

export const listOrderDocuments = async (orderId: string): Promise<OrderDocument[]> => {
  const docsRef = collection(db, 'orders', orderId, 'documents');
  const docsQuery = query(docsRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(docsQuery);

  return snapshot.docs.map((docSnap) => mapOrderDocument(orderId, docSnap.id, docSnap.data()));
};

export const createOrderDocument = async ({
  orderId,
  file,
  type,
  title,
  uploadedByName,
}: CreateOrderDocumentInput): Promise<OrderDocument> => {
  validateFile(file);

  const docsRef = collection(db, 'orders', orderId, 'documents');
  const documentRef = doc(docsRef);
  const sanitizedFileName = sanitizeFileName(file.name);
  const storagePath = `orders/${orderId}/documents/${documentRef.id}/${sanitizedFileName}`;
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, file.type ? { contentType: file.type } : undefined);
  const downloadUrl = await getDownloadURL(storageRef);

  const documentData = {
    orderId,
    type,
    title: title?.trim() || sanitizedFileName,
    fileName: sanitizedFileName,
    storagePath,
    downloadUrl,
    contentType: file.type || '',
    sizeBytes: file.size,
    uploadedByUid: auth.currentUser?.uid || null,
    uploadedByName: uploadedByName?.trim() || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(documentRef, documentData);

  return mapOrderDocument(orderId, documentRef.id, {
    ...documentData,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

export const updateOrderDocument = async (
  orderId: string,
  documentId: string,
  patch: UpdateOrderDocumentInput
): Promise<void> => {
  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (patch.type) {
    updateData.type = patch.type;
  }
  if (patch.title !== undefined) {
    const normalizedTitle = patch.title.trim();
    updateData.title = normalizedTitle || 'Untitled document';
  }

  await updateDoc(doc(db, 'orders', orderId, 'documents', documentId), updateData);
};

export const deleteOrderDocument = async (orderId: string, orderDocument: OrderDocument): Promise<void> => {
  if (orderDocument.storagePath) {
    try {
      await deleteObject(ref(storage, orderDocument.storagePath));
    } catch (error) {
      if (!(error instanceof FirebaseError) || error.code !== 'storage/object-not-found') {
        throw error;
      }
    }
  }

  await deleteDoc(doc(db, 'orders', orderId, 'documents', orderDocument.id));
};

